import React, { useMemo } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { AppDispatch, RootStateType } from '@Store';

import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';

import { vendorAccountType } from '@Reducers/AdminShipment/Type';

import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';

import { setCarrierSettings } from '@Reducers/User';

import {
  TermsOfDeliveryOptions,
  ExportReasonOptions,
  CurrencyOptions,
  InvoiceTypeOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Dpduk/constants';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}

const dpdukValidationSchema = Yup.object().shape({
  accountNo: Yup.array().min(1, 'Account is required').required(),

  termsOfDelivery: Yup.array().min(1, 'Terms Of Delivery is required').required(),

  exportReason: Yup.array().min(1, 'Export Reason is required').required(),

  currency: Yup.array().min(1, 'Currency is required').required(),

  invoiceType: Yup.array().min(1, 'Invoice Type is required').required(),
});

const Dpduk = ({ onClose, isEditable }: propsTypes) => {
  const dispatch = useDispatch<AppDispatch>();

  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);

  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  const { values, setFieldValue, errors, handleSubmit } = useFormik<any>({
    enableReinitialize: true,

    validateOnChange: false,

    validationSchema: dpdukValidationSchema,

    initialValues: {
      ...carrierSetting.dpduk,
    },

    onSubmit(values) {
      const finalPayload = {
        ...carrierSetting.dpduk,
        ...values,
      };

      if (userDataById?._id) {
        if (carrierSetting.dpduk?._id) {
          dispatch(updateCarrierSettings(finalPayload));
        } else {
          dispatch(addCarrierSettings(finalPayload));
        }
      } else {
        dispatch(
          setCarrierSettings({
            values: finalPayload,
            carrierType: 'dpduk',
          }),
        );
      }

      onClose();
    },
  });

  return (
    <Paper
      variant="outlined"
      sx={{
        my: '2rem',
        p: '2rem',
      }}
    >
      <Grid container>
        <Grid item xs={12} mb={3}>
          <Typography className="font-medium l-text">DPDUK Information</Typography>
        </Grid>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              label="DPDUK Account"
              multiple
              required
              value={values.accountNo?.map((item: any) => item.value) || []}
              options={accountOptions}
              onChange={(_, selected) => setFieldValue('accountNo', selected)}
              error={errors.accountNo as string}
              id="accountNo"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              multiple
              required
              label="Invoice Type"
              value={values.invoiceType?.map((i: any) => i.value) || []}
              options={InvoiceTypeOptions}
              onChange={(_, selected) => setFieldValue('invoiceType', selected)}
              error={errors.invoiceType as string}
              id="invoiceType"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              multiple
              required
              label="Terms Of Delivery"
              value={values.termsOfDelivery?.map((i: any) => i.value) || []}
              options={TermsOfDeliveryOptions}
              onChange={(_, selected) => setFieldValue('termsOfDelivery', selected)}
              error={errors.termsOfDelivery as string}
              id="termsOfDelivery"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              multiple
              required
              label="Export Reason"
              value={values.exportReason?.map((i: any) => i.value) || []}
              options={ExportReasonOptions}
              onChange={(_, selected) => setFieldValue('exportReason', selected)}
              error={errors.exportReason as string}
              id="exportReason"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              multiple
              required
              label="Currency"
              value={values.currency?.map((i: any) => i.value) || []}
              options={CurrencyOptions}
              onChange={(_, selected) => setFieldValue('currency', selected)}
              error={errors.currency as string}
              id="currency"
            />
          </Grid>
        </Grid>

        <Divider className="mt-3 mb-3" />

        <Grid container sx={{ gap: 2 }}>
          <Grid item xs={12} sm={5} md={3}>
            <SbButton disabled={!isEditable} variant="contained" className="w-full" onClick={() => handleSubmit()}>
              Save
            </SbButton>
          </Grid>

          <Grid item xs={12} sm={5} md={3}>
            <SbButton variant="outlined" className="w-full" onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Dpduk;
